import { useState, useEffect } from 'react';
import { MapPin, Calendar, Clock, TrendingUp, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Hero = ({ onBookNow }) => {
  const [address, setAddress] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [bookingType, setBookingType] = useState('now');
  const [services, setServices] = useState([]);
  const [availability, setAvailability] = useState(null);
  const [priceEstimate, setPriceEstimate] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const [liveStats, setLiveStats] = useState({ total_bookings: 0, active_bookings: 0 });
  const [city, setCity] = useState('Lagos');

  useEffect(() => {
    fetchServices();
    fetchLiveStats();
    const statsInterval = setInterval(fetchLiveStats, 30000); // Update every 30s
    return () => clearInterval(statsInterval);
  }, []);

  useEffect(() => {
    if (selectedService && city) {
      checkAvailabilityLive();
      estimatePrice();
    }
  }, [selectedService, city]);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchLiveStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setLiveStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const checkAvailabilityLive = async () => {
    if (!selectedService) return;
    
    setIsChecking(true);
    try {
      const response = await axios.post(`${API}/availability/check`, {
        city: city,
        service_id: selectedService,
        date: new Date().toISOString().split('T')[0],
        time: new Date().toTimeString().split(' ')[0].substring(0, 5)
      });
      setAvailability(response.data);
      
      if (response.data.availability_status === 'high') {
        toast.success('Great! High availability in your area');
      }
    } catch (error) {
      console.error('Error checking availability:', error);
    } finally {
      setIsChecking(false);
    }
  };

  const estimatePrice = async () => {
    if (!selectedService) return;
    
    try {
      const response = await axios.post(`${API}/estimate/price`, {
        service_id: selectedService,
        location_type: 'home',
        city: city
      });
      setPriceEstimate(response.data);
    } catch (error) {
      console.error('Error estimating price:', error);
    }
  };

  const handleFindBarber = () => {
    const service = services.find(s => s.id === selectedService);
    if (!selectedService) {
      toast.error('Please select a service first');
      return;
    }
    if (!address) {
      toast.error('Please enter your address');
      return;
    }
    onBookNow(service, {
      address,
      bookingType,
      estimatedPrice: priceEstimate?.estimated_price
    });
  };

  const getAvailabilityColor = (status) => {
    switch(status) {
      case 'high': return 'text-green-400';
      case 'medium': return 'text-yellow-400';
      case 'low': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  return (
    <section id="hero" data-testid="hero-section" className="relative min-h-screen flex items-center pt-16">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img 
          src="https://images.unsplash.com/photo-1771489465791-00ee339a9c92?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxibGFjayUyMGJhcmJlciUyMGN1dHRpbmclMjBoYWlyfGVufDB8fHx8MTc3NDUyNTE2N3ww&ixlib=rb-4.1.0&q=85"
          alt="Professional barber service"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#111111]/95 via-[#111111]/80 to-[#111111]/60"></div>
      </div>

      {/* Content */}
      <div className="container relative z-10 py-20">
        <div className="max-w-3xl">
          {/* Trust Badge with Live Stats */}
          <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6" data-testid="trust-badge">
            <span className="text-yellow-400 text-sm">⭐</span>
            <span className="text-white text-sm font-medium">4.9 from {liveStats.total_bookings.toLocaleString()} bookings</span>
            {liveStats.active_bookings > 0 && (
              <>
                <span className="text-white/40">•</span>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                  <span className="text-green-400 text-sm font-medium">{liveStats.active_bookings} live bookings</span>
                </div>
              </>
            )}
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter text-white mb-6 leading-[1.1] animate-fade-in" data-testid="hero-headline">
            Fresh cuts,<br />zero travel
          </h1>

          {/* Subheadline */}
          <p className="text-lg sm:text-xl text-gray-300 mb-10 leading-relaxed" data-testid="hero-subheadline">
            Book a professional barber to your home or office in minutes
          </p>

          {/* Booking Card */}
          <div className="glass-card p-6 md:p-8 space-y-6" data-testid="booking-card">
            {/* Address Input */}
            <div>
              <label className="block text-sm font-semibold text-white mb-2">Where are you?</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  data-testid="address-input"
                  type="text"
                  placeholder="Enter your home or office address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="pl-11 h-12 bg-[#1A1A1A] border-white/10 text-white placeholder:text-gray-500 focus:border-[#00C167] focus:ring-[#00C167]/20 transition-all"
                />
              </div>
            </div>

            {/* Service Selection */}
            <div>
              <label className="block text-sm font-semibold text-white mb-3">Select a service</label>
              <div className="flex flex-wrap gap-2" data-testid="service-chips">
                {services.map(service => (
                  <button
                    key={service.id}
                    data-testid={`service-chip-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => setSelectedService(service.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all transform hover:scale-105 ${
                      selectedService === service.id
                        ? 'bg-[#00C167] text-white shadow-lg shadow-[#00C167]/30'
                        : 'bg-white/10 text-gray-300 hover:bg-white/20'
                    }`}
                  >
                    {service.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Live Availability & Price Info */}
            {selectedService && (
              <div className="bg-white/5 border border-white/10 rounded-lg p-4 space-y-3 animate-fade-in">
                <div className="grid grid-cols-2 gap-4">
                  {/* Availability */}
                  {availability && (
                    <div className="space-y-1">
                      <div className="text-xs text-gray-400 uppercase tracking-wider">Availability</div>
                      <div className={`text-lg font-bold ${getAvailabilityColor(availability.availability_status)}`}>
                        {isChecking ? '...' : availability.estimated_wait_time}
                      </div>
                      <div className="text-xs text-gray-400">
                        {availability.slots_available} barbers nearby
                      </div>
                    </div>
                  )}
                  
                  {/* Price Estimate */}
                  {priceEstimate && (
                    <div className="space-y-1">
                      <div className="text-xs text-gray-400 uppercase tracking-wider">Estimated Price</div>
                      <div className="text-lg font-bold text-[#00C167]">
                        ₦{priceEstimate.estimated_price.toLocaleString()}
                      </div>
                      {priceEstimate.surge_multiplier > 1 && (
                        <div className="flex items-center gap-1 text-xs text-orange-400">
                          <TrendingUp className="w-3 h-3" />
                          High demand ({priceEstimate.surge_multiplier}x)
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Booking Time */}
            <div>
              <label className="block text-sm font-semibold text-white mb-3">When do you need it?</label>
              <div className="grid grid-cols-2 gap-3" data-testid="booking-time-options">
                <button
                  data-testid="booking-now-button"
                  onClick={() => setBookingType('now')}
                  className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all transform hover:scale-105 ${
                    bookingType === 'now'
                      ? 'bg-[#00C167] text-white shadow-lg shadow-[#00C167]/30'
                      : 'bg-white/10 text-gray-300 hover:bg-white/20'
                  }`}
                >
                  <Clock className="w-4 h-4" />
                  Book Now
                </button>
                <button
                  data-testid="booking-later-button"
                  onClick={() => setBookingType('later')}
                  className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all transform hover:scale-105 ${
                    bookingType === 'later'
                      ? 'bg-[#00C167] text-white shadow-lg shadow-[#00C167]/30'
                      : 'bg-white/10 text-gray-300 hover:bg-white/20'
                  }`}
                >
                  <Calendar className="w-4 h-4" />
                  Schedule Later
                </button>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button
                data-testid="find-barber-button"
                onClick={handleFindBarber}
                className="flex-1 h-12 bg-[#00C167] hover:bg-[#009E54] text-white font-semibold text-base shadow-lg shadow-[#00C167]/30 hover:shadow-xl hover:shadow-[#00C167]/40 transition-all transform hover:scale-105"
              >
                Find a Barber Now
              </Button>
              <Button
                data-testid="become-barber-hero-button"
                onClick={() => document.getElementById('for-barbers')?.scrollIntoView({ behavior: 'smooth' })}
                variant="outline"
                className="flex-1 h-12 border-white/20 text-white hover:bg-white/10 hover:border-white/40 font-semibold text-base transition-all transform hover:scale-105"
              >
                Become a Barber
              </Button>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
      `}</style>
    </section>
  );
};

export default Hero;